var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { usePrintCopyDialog } from '~/pages/details/common/get-this-title-files/options';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOpenGiveDialog } from '~/pages/details/common/get-this-title-files/give-before-pdf/use-give-dialog';
import { useToggle } from '~/helpers/data';
import { fetchAllBooks } from '~/models/books';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown';
export default function GetTheBookDropdown({ bookInfo }) {
    const ref = React.useRef(null);
    const { slug } = bookInfo;
    const buttonId = `${slug}-ddb`;
    const menuId = `${slug}-ddm`;
    const webviewLink = bookInfo.webviewRexLink || bookInfo.webviewLink;
    const pdfLink = bookInfo.highResolutionPdfUrl || bookInfo.lowResolutionPdfUrl;
    const warning = useWarning(bookInfo.id);
    return (React.createElement("div", { className: "navmenu", ref: ref, "data-analytics-nav": "Get the book dropdown" },
        React.createElement(ControlButton, { parentRef: ref, buttonId: buttonId, menuId: menuId }),
        React.createElement("div", { id: menuId, role: "menu", "aria-labelledby": buttonId },
            React.createElement(MenuItemWithGiveDialog, { defaultMessage: "View online", url: webviewLink, variant: "View online", warning: warning, id: bookInfo.id.toString() }),
            React.createElement(MenuItemWithGiveDialog, { defaultMessage: "Download a PDF", url: pdfLink, warning: warning, id: bookInfo.id.toString() }),
            React.createElement(PrintOption, { slug: bookInfo.slug }),
            React.createElement("hr", null),
            React.createElement(MenuItem, { defaultMessage: "Instructor resources", url: `/details/${slug}?Instructor resources` }),
            React.createElement(MenuItem, { defaultMessage: "Student resources", url: `/details/${slug}?Student resources` }))));
}
function useWarning(id) {
    const [text, setText] = React.useState('');
    React.useEffect(() => {
        fetchAllBooks.then((books) => {
            const entry = books.find((b) => b.id === id);
            if (entry === null || entry === void 0 ? void 0 : entry.content_warning_text) {
                setText(entry.content_warning_text);
            }
        });
    }, [id]);
    return text;
}
function ControlButton({ parentRef, buttonId, menuId }) {
    const [isOpen, toggle] = useToggle();
    const toggleMenu = React.useCallback(() => {
        toggle();
    }, [toggle]);
    // This is supposed to close it when another opens
    React.useEffect(() => {
        const outsideClick = (e) => {
            var _a;
            if (isOpen && !((_a = parentRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                toggle();
            }
        };
        window.addEventListener('click', outsideClick);
        return () => window.removeEventListener('click', outsideClick);
    }, [isOpen, toggle, parentRef]);
    return (React.createElement("button", { id: buttonId, type: "button", "aria-haspopup": "true", "aria-controls": menuId, "aria-expanded": isOpen, onClick: toggleMenu },
        React.createElement(FormattedMessage, { id: "getTheBook", defaultMessage: "Get the book" }),
        React.createElement(FontAwesomeIcon, { icon: isOpen ? faCaretUp : faCaretDown })));
}
function FormattedMessageFor({ defaultMessage }) {
    if (defaultMessage === 'View online') {
        return (React.createElement(FormattedMessage, { id: "getit.webview.link", defaultMessage: "View online" }));
    }
    if (defaultMessage === 'Download a PDF') {
        return (React.createElement(FormattedMessage, { id: "getit.pdf.download", defaultMessage: "Download a PDF" }));
    }
    if (defaultMessage === 'Instructor resources') {
        return (React.createElement(FormattedMessage, { id: "tabs.instructorResources", defaultMessage: "Instructor resources" }));
    }
    return (React.createElement(FormattedMessage, { id: "tabs.studentResources", defaultMessage: "Student resources" }));
}
function MenuItem(_a) {
    var { defaultMessage, url } = _a, aProps = __rest(_a, ["defaultMessage", "url"]);
    return (React.createElement("a", Object.assign({ role: "menuitem", href: url }, aProps),
        React.createElement(FormattedMessageFor, { defaultMessage: defaultMessage })));
}
function MenuItemWithGiveDialog(_a) {
    var { variant, warning, id } = _a, props = __rest(_a, ["variant", "warning", "id"]);
    const { GiveDialog, openGiveDialog } = useOpenGiveDialog();
    return (React.createElement(React.Fragment, null,
        React.createElement(MenuItem, Object.assign({}, props, { onClick: openGiveDialog })),
        React.createElement(GiveDialog, { link: props.url, variant: variant, warning: warning, id: id })));
}
function PrintOption({ slug }) {
    const { onClick, PCDialog } = usePrintCopyDialog();
    const { formatMessage } = useIntl();
    const text = formatMessage({
        id: 'getit.print',
        defaultMessage: 'Order a print copy'
    });
    return (React.createElement("a", { role: "menuitem", href: "open a dialog", onClick: onClick },
        text,
        React.createElement(PCDialog, { text: text, slug: slug })));
}
